import { isRectType, MIN_RECT_WIDTH_SCALE, rectDefaultWidthScale } from './rect-tbc';
import { padLegEndpoints } from './shaped-symbol-geometry';
import { defaultWidthScale, isFullVariant, isLegCapType, type TbcType } from './tbc-types';

/** Floor for the auto-derived widthScale — keeps very dense runs of stitches
 *  from degenerating to invisible slivers. */
export const MIN_AUTO_WIDTH_SCALE = 0.05;

/** Manual stitch-height bounds (multiplier on natural rendered height) —
 *  the size-slider range. */
export const MIN_STITCH_HEIGHT_SCALE = 0.25;
export const MAX_STITCH_HEIGHT_SCALE = 3;

/** Manual stitch-width bounds (factor on the type's natural rendered width) —
 *  the size-slider range. Mirrors the height-scale bounds; 1.0 = natural
 *  width. */
export const MIN_STITCH_WIDTH_SCALE = 0.25;
export const MAX_STITCH_WIDTH_SCALE = 3;

/** The type's natural (default) widthScale — the absolute value that 1.0× of
 *  the manual width factor maps to, and the denominator the aspect-lock height
 *  derivation normalizes against. Rect glyphs use the rect default; everything
 *  else uses the shaped default. */
export function naturalWidthScale(type: TbcType): number {
    return isRectType(type) ? rectDefaultWidthScale(type) : defaultWidthScale(type);
}

/** Absolute widthScale for a manual width factor (1.0 = natural). Clamps the
 *  factor to the slider range, then floors the result at the rect minimum.
 *  Shared by every manual-width and aspect-lock width-recalc site so the
 *  clamp/floor lives in one place. */
export function widthScaleFromFactor(type: TbcType, factor: number): number {
    const clamped = Math.max(MIN_STITCH_WIDTH_SCALE, Math.min(MAX_STITCH_WIDTH_SCALE, factor));
    return Math.max(MIN_RECT_WIDTH_SCALE, clamped * naturalWidthScale(type));
}

/** Auto-width symbols target this fraction of the pitch, leaving a ~12%
 *  proportional gap between neighbors. The gap scales with density, so
 *  sparse and dense runs keep the same visual rhythm. */
export const AUTO_WIDTH_FILL_FACTOR = 0.88;

/** Open shapes span endpoints-included (N−1 gaps); closed loops wrap (N gaps).
 *  Single source for the spacing convention used by spacingFractions and the
 *  auto-width pitch. */
export function spacingDivisor(count: number, closedLoop: boolean): number {
    return closedLoop ? count : Math.max(1, count - 1);
}

/** Even arc-length fractions in [0,1]. Open shapes include both endpoints
 *  (i/(N-1)); closed loops use i/N so no point doubles at the seam. */
export function spacingFractions(count: number, closedLoop: boolean): number[] {
    const d = spacingDivisor(count, closedLoop);
    return Array.from({ length: count }, (_, i) => i / d);
}

/** Per-type auto-width fill factor. The chain family tiles edge-to-edge (a
 *  chain row reads as touching loops), so 'chain' and 'chain-full' get no
 *  breathing gap; every other width-scalable type keeps the proportional
 *  gap. */
export function autoWidthFillFactorFor(type: TbcType): number {
    return type === 'chain' || type === 'chain-full' ? 1 : AUTO_WIDTH_FILL_FACTOR;
}

/** widthScale that makes `type`'s symbol fill one `pitch` of spacing, given the
 *  base→top extent `baseToTop` the width formula is expressed against. Wraps the
 *  fill factor + body-extent + inversion so every auto-width path (including the
 *  add-stitches-along-guide bake path) shares one formula. Returns null when not
 *  applicable (leg-cap family ignores widthScale, or a degenerate pitch). */
export function autoWidthScaleForPitch(type: TbcType, pitch: number, baseToTop: number): number | null {
    if (isLegCapType(type)) return null;
    const bodyExtent = autoWidthBodyExtent(type, baseToTop);
    return invertAutoWidth(type, pitch * autoWidthFillFactorFor(type), bodyExtent);
}

/** The body extent the width formula is expressed against. The shaped
 *  renderer insets the body endpoints by the cosmetic pad
 *  (padPolicyFor/padLegEndpoints) BEFORE computing the ellipse axes, so
 *  invert against the padded |base→top| or symbols under-fill the pitch.
 *  Stitches are always symmetric 1-base-1-top here: midpoint variants
 *  pad the top side only; -full variants pad both. Rect types draw from
 *  raw geometry — no pad. Mirrors shaped-symbol-geometry's
 *  padLegEndpoints incl. its proportional clamp for short legs. */
export function autoWidthBodyExtent(type: TbcType, baseToTop: number): number {
    if (isRectType(type)) return baseToTop;
    // Pads mirror padPolicyFor's symmetric branches (shaped-symbol-
    // geometry.ts): shaped non-full → {padBase: false, padTop: true};
    // -full → {padBase: true, padTop: true}. Measured on a synthetic
    // collinear leg so padLegEndpoints' length-proportional magnitude and
    // short-leg clamp apply just as they do at render time.
    const padBase = isFullVariant(type);
    const padded = padLegEndpoints(
        { x: 0, y: 0 },
        { x: 0, y: baseToTop },
        padBase,
        true,
    );
    return padded.top.y - padded.base.y;
}

/** Invert a target rendered width to a widthScale per renderer family:
 *  - rect glyphs (every type except leg-cap family, per isRectType): width =
 *    widthScale·|base→top| regardless of variant — the -full halving must NOT
 *    apply (a 'span' anchor stretches the rect across the whole extent).
 *  - shaped ellipses (the asymmetric leg path): width = 2·b·widthScale with
 *    b = padded extent for midpoint variants, padded extent / 2 for -full.
 *  Null on degenerate geometry (incl. pad-consumed legs); 1e-6 = float
 *  headroom on the divisor, not a visual threshold. Shared by every
 *  auto-width path so they cannot drift. */
export function invertAutoWidth(type: TbcType, targetWidth: number, bodyExtent: number): number | null {
    if (!Number.isFinite(targetWidth) || targetWidth <= 0 || bodyExtent <= 1e-6) return null;
    const ws = isRectType(type)
        ? targetWidth / bodyExtent
        : targetWidth / (2 * (isFullVariant(type) ? bodyExtent / 2 : bodyExtent));
    return Math.max(MIN_AUTO_WIDTH_SCALE, ws);
}
